import React from 'react'
import { api, formatDate } from '../../utils/api'
import ReverbThreadSection from './helpers/ReverbThreadSection.jsx'
import EmailContent from './helpers/EmailContent.jsx'
import AttachmentThumbnail from './helpers/AttachmentThumbnail.jsx'
import DownloadAllAttachments from './helpers/DownloadAllAttachments.jsx'
import PhoneComposer from './PhoneComposer.jsx'
import PhoneMenuPopover from './PhoneMenuPopover.jsx'
import PresenceBanner from './PresenceBanner.jsx'
import StaleReplyNotice from './StaleReplyNotice.jsx'
import TopicPicker from './TopicPicker.jsx'
import FaqLinkPicker from './FaqLinkPicker.jsx'
import { replyAuthorLabel } from './replyAuthor.js'
import { ticketOrderRef } from './ticketOrderRef.js'
import { emailTimeLines } from './emailTimes.js'
import { activeUsersFrom } from '../../../../shared/users.js'
import DealLink from '../shared/DealLink.jsx'

// Open a ticket's linked order directly in the orders list (new tab). Targets
// the /orders/list leaf route with orderId so OrdersList auto-opens the order
// detail rather than landing on the generic, unfiltered list.
function openTicketOrder(orderRef) {
  window.open(`/orders/list?orderId=${encodeURIComponent(orderRef)}`, '_blank')
}

// Status colour family. Falls back to "neutral" for unknown statuses.
// `waiting` is a generic catch-all that backends sometimes return for
// not-yet-specialised waiting states; treat it the same as the more
// specific waiting_customer / waiting_third_party.
const STATUS_VARIANT = {
  new: 'warn',
  awaiting_reply: 'warn',
  resolved: 'ok',
  closed: 'ok',
  waiting: 'wait',
  waiting_customer: 'wait',
  waiting_third_party: 'wait',
  service: 'neutral',
  spam: 'neutral',
  offers: 'neutral'
}

// "Email sent" and "Synced to Prism" lines above a customer email.
function EmailTimes({ row }) {
  return emailTimeLines(row).map(line => <div key={line}>{line}</div>)
}

function PhoneConversationHeader({ ticket, onBack, onOpenMenu, onOpenStatusPicker, menuRef, statusRef }) {
  if (!ticket) return null
  const variant = STATUS_VARIANT[ticket.status] || 'neutral'
  return (
    <div className="conv-head">
      <button className="icon-btn" onClick={onBack} aria-label="Back to inbox">←</button>
      <div className="meta">
        <div className="sub">{ticket.subject || 'No subject'}</div>
        <div className="from">{ticket.from_name || ticket.from_email || ticket.customer_email}</div>
      </div>
      {/* Status pill is a button — taps open the status picker. Spec D16. */}
      <button
        ref={statusRef}
        className={`status-pill status-pill--${variant}`}
        onClick={onOpenStatusPicker}
        aria-label={`Change status (currently ${ticket.status?.replace(/_/g, ' ') || 'unknown'})`}
      >
        {ticket.status?.replace(/_/g, ' ')} ▾
      </button>
      <button ref={menuRef} className="icon-btn" onClick={onOpenMenu} aria-label="More actions">⋮</button>
    </div>
  )
}

// D17 — case bar of channel + linked-order + linked-deal pills below the
// conversation header. Tappable order/deal pills navigate to the
// respective module. Order pill is always shown; the linked pills only
// render when the ticket has an id in that domain.
function PhoneCaseBar({ ticket }) {
  if (!ticket) return null
  const channel = ticket.channel || 'email'

  const channelLabel = channel === 'ebay' ? 'eBay' : channel === 'reverb' ? 'Reverb' : 'Email'
  const orderRef = ticketOrderRef(ticket)

  return (
    <div className="case-bar" role="toolbar" aria-label="Ticket case">
      <span className={`case-pill case-pill--${channel}`}>{channelLabel}</span>
      {orderRef && (
        <button
          className="case-pill case-pill--order"
          onClick={() => openTicketOrder(orderRef)}
          aria-label={`Open linked order ${orderRef}`}
        >
          Order #{orderRef}
        </button>
      )}
      {ticket.deal_id && (
        // A new tab, so a reply being written here is not lost.
        <span className="case-pill case-pill--deal">
          Deal <DealLink dealId={ticket.deal_id} newTab className="deal-link--inherit" />
        </span>
      )}
    </div>
  )
}

/**
 * Conversation view — the detail wrapper containing the assignment bar,
 * the message thread (conversationColumn), and the quick-reply composer
 * (replyColumn). State lives in the orchestrator (Tickets.jsx) and is
 * passed in via props. JSX is moved verbatim from the orchestrator —
 * desktop visual must be byte-identical.
 */
function TicketConversation({
  // Layout / phone mode
  breakpoint, mobileV2 = false,
  composerOpen, setComposerOpen,
  onBackToInbox,
  // Visibility / sizing
  isMobile, mobileView, setMobileView,
  // Ticket data
  selectedTicket, ticketDetails, loadingDetails,
  // Presence + stale-reply refusal (see useTicketPresence / StaleReplyNotice)
  viewers, staleReply, clearStaleReply,
  // Reverb thread state + setters
  reverbThread, reverbThreadStatus, reverbThreadReason, reverbThreadFetchedAt, reverbThreadRefreshing,
  setReverbThread, setReverbThreadStatus, setReverbThreadReason,
  setReverbThreadFetchedAt, setReverbThreadRefreshing,
  // Composer state
  replyText, setReplyText, sending,
  replyFile, setReplyFile,
  aiLoading, rewriteLoading, rewriteContext, setRewriteContext,
  suggestTemplate, setSuggestTemplate, quoteChoices,
  // Pin modal triggers
  setShowPinModal, setPinData, setPinUsers,
  // User / assignment
  user, ticketUsers, getUserName, handleAssign, handleTopicChange,
  // Reply / status handlers
  getSuggestion, getRewrite, updateTicketStatus, sendReply,
  markAsService, ignoreSender
}) {
  // M2 — single "active popover" enum so the conv menu and the status
  // picker can never be both-open at the same time. Opening one closes
  // the other automatically. Hooks stay above the early return below.
  const [activePopover, setActivePopover] = React.useState(null) // null | 'menu' | 'status'
  const convMenuBtnRef = React.useRef(null)
  const statusPillRef = React.useRef(null)
  // A popover belongs to one ticket: close it on Back or on a switch,
  // before paint, so the old menu never flashes over the new ticket.
  React.useLayoutEffect(() => { setActivePopover(null) }, [selectedTicket?.id])

  // The orchestrator previously gated the entire detail wrapper on
  // `selectedTicket`; preserve that behaviour here so an empty
  // <TicketConversation /> doesn't add a flex item to the layout when
  // no ticket is open.
  if (!selectedTicket) return null

  // "You" only for my own replies; a colleague's reply carries their name.
  const authorOf = reply => replyAuthorLabel(reply, {
    currentUserId: user?.user_id,
    ticketUsers,
    customerName: ticketDetails?.ticket?.from_name
  })
  const composerNotices = (
    <>
      <StaleReplyNotice notice={staleReply} currentUserId={user?.user_id} ticketUsers={ticketUsers} onDismiss={clearStaleReply} />
      <PresenceBanner viewers={viewers} />
      {quoteChoices && (
        <label className="text-muted" style={{ display: 'block', fontSize: 12, marginBottom: 'var(--space-2)' }}>
          Which quote is this counter offer for?
          <select
            className="form-input"
            aria-label="Counter offer quote"
            value=""
            onChange={e => getSuggestion(e.target.value)}
            disabled={aiLoading}
            style={{ marginTop: 'var(--space-1)' }}
          >
            <option value="" disabled>Choose a quote…</option>
            {quoteChoices.map(q => <option key={q.id} value={q.id}>{q.label}</option>)}
          </select>
        </label>
      )}
    </>
  )

  // The chat-bubble chrome (back-arrow header + thread + collapsed reply
  // bar) is used on BOTH phone and tablet when mobile-v2 is on — tablet
  // just renders it inside a wider flex column (TabletTicketsLayout owns
  // that column). Variable kept named `isPhone` to minimise diff against
  // the existing branch.
  const isPhone = (breakpoint === 'phone' || breakpoint === 'tablet') && mobileV2

  // Phone/tablet variant: chat-bubble header + thread body + collapsed
  // reply bar (expands to PhoneComposer). Reuses the existing
  // message-thread JSX — only the surrounding chrome changes.
  if (isPhone) {
    const displayTicket = ticketDetails?.ticket || selectedTicket
    const convMenuItems = [
      {
        label: 'Pin ticket',
        onClick: () => {
          setShowPinModal(true)
          setPinData({ reason: '', dueDate: '', assignedTo: '' })
          if (user?.is_admin) {
            api.get('/api/users').then(res => {
              setPinUsers(activeUsersFrom(res))
            }).catch(err => console.error('Failed to load users:', err))
          }
        }
      },
      { label: 'Mark sender as Service (email)', onClick: () => markAsService('email') },
      { label: 'Mark sender as Service (domain)', onClick: () => markAsService('domain') },
      { label: 'Block email', onClick: () => ignoreSender('email'), danger: true },
      { label: 'Block domain', onClick: () => ignoreSender('domain'), danger: true }
    ]
    return (
      <div className="conv--phone" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <PhoneConversationHeader
          ticket={displayTicket}
          onBack={onBackToInbox}
          onOpenMenu={() => setActivePopover('menu')}
          onOpenStatusPicker={() => setActivePopover('status')}
          menuRef={convMenuBtnRef}
          statusRef={statusPillRef}
        />
        <PhoneCaseBar ticket={displayTicket} />
        <PhoneMenuPopover
          open={activePopover === 'menu'}
          onClose={() => setActivePopover(null)}
          anchorRef={convMenuBtnRef}
          items={convMenuItems}
        />
        <PhoneMenuPopover
          open={activePopover === 'status'}
          onClose={() => setActivePopover(null)}
          anchorRef={statusPillRef}
          items={[
            { label: 'Mark Resolved', onClick: () => updateTicketStatus(selectedTicket.id, 'resolved') },
            { label: 'Waiting on customer', onClick: () => updateTicketStatus(selectedTicket.id, 'waiting_customer') },
            { label: 'Waiting on 3rd party', onClick: () => updateTicketStatus(selectedTicket.id, 'waiting_third_party') },
            { label: 'Reopen', onClick: () => updateTicketStatus(selectedTicket.id, 'new') }
          ]}
        />
        <div style={{ flex: 1, overflowY: 'auto', padding: 12, background: '#f5f5f7' }}>
          {loadingDetails ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div className="loading-spinner" />
            </div>
          ) : ticketDetails && (
            <>
              {ticketDetails.ticket.channel === 'reverb' && (
                <ReverbThreadSection
                  compact
                  thread={reverbThread}
                  status={reverbThreadStatus}
                  reason={reverbThreadReason}
                  fetchedAt={reverbThreadFetchedAt}
                  refreshing={reverbThreadRefreshing}
                  onRefresh={async () => {
                    setReverbThreadRefreshing(true)
                    try {
                      const res = await api.post(`/api/tickets/${ticketDetails.ticket.id}/reverb-thread/refresh`)
                      if (res?.thread) {
                        setReverbThread(res.thread)
                        setReverbThreadFetchedAt(res.fetchedAt || null)
                        setReverbThreadStatus('ready')
                        setReverbThreadReason(null)
                      } else {
                        setReverbThreadStatus('unavailable')
                        setReverbThreadReason(res?.reason || 'no thread available')
                      }
                    } catch (err) {
                      setReverbThreadStatus('error')
                      setReverbThreadReason(err.message || 'request failed')
                    } finally {
                      setReverbThreadRefreshing(false)
                    }
                  }}
                />
              )}
              {[...(ticketDetails.replies || [])].reverse().map(reply => (
                <div
                  key={reply.id}
                  style={{
                    background: reply.direction === 'out' ? '#dbeafe' : 'white',
                    padding: 12,
                    borderRadius: 8,
                    marginBottom: 12,
                    marginLeft: reply.direction === 'out' ? 24 : 0,
                    marginRight: reply.direction === 'in' ? 24 : 0
                  }}
                >
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                    {reply.direction === 'in'
                      ? <>{authorOf(reply)}<EmailTimes row={reply} /></>
                      : <>{authorOf(reply)} · {formatDate(reply.sent_at)}</>}
                  </div>
                  <EmailContent html={reply.body_html} text={reply.body_text} />
                  {reply.attachments && (() => {
                    const attachments = typeof reply.attachments === 'string'
                      ? JSON.parse(reply.attachments)
                      : reply.attachments
                    return attachments.length > 0 && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                          Attachments ({attachments.length})
                          {attachments.filter(a => a.attachmentId).length > 1 && (
                            <DownloadAllAttachments ticketId={ticketDetails.ticket.id} attachments={attachments} />
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {attachments.map((att, idx) => (
                            <AttachmentThumbnail key={idx} ticketId={ticketDetails.ticket.id} attachment={att} />
                          ))}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              ))}
              <div style={{ background: 'white', padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
                  <EmailTimes row={ticketDetails.ticket} />
                </div>
                <EmailContent
                  html={ticketDetails.ticket.body_html}
                  text={ticketDetails.ticket.body_text}
                  fallback={ticketDetails.ticket.snippet}
                />
                {ticketDetails.ticket.attachments && (() => {
                  const attachments = typeof ticketDetails.ticket.attachments === 'string'
                    ? JSON.parse(ticketDetails.ticket.attachments)
                    : ticketDetails.ticket.attachments
                  return attachments.length > 0 && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                        Attachments ({attachments.length})
                        {attachments.filter(a => a.attachmentId).length > 1 && (
                          <DownloadAllAttachments ticketId={ticketDetails.ticket.id} attachments={attachments} />
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {attachments.map((att, idx) => (
                          <AttachmentThumbnail key={idx} ticketId={ticketDetails.ticket.id} attachment={att} />
                        ))}
                      </div>
                    </div>
                  )
                })()}
              </div>
            </>
          )}
        </div>
        {composerNotices}
        {!composerOpen ? (
          <div className="conv-replybar">
            {/* 📎 in the collapsed bar opens the composer so the file
                picker lives in one place (the composer). Keeps the
                attach flow consistent regardless of where the user taps. */}
            <button className="icon-btn" aria-label="Attach file and reply" onClick={() => setComposerOpen(true)}>📎</button>
            <button className="conv-reply-cta" onClick={() => setComposerOpen(true)}>Reply</button>
            <button className="icon-btn" aria-label="AI suggest reply" onClick={getSuggestion} disabled={aiLoading}>
              {aiLoading ? '…' : '🤖'}
            </button>
          </div>
        ) : (
          <PhoneComposer
            replyText={replyText}
            setReplyText={setReplyText}
            sending={sending}
            replyFile={replyFile}
            setReplyFile={setReplyFile}
            aiLoading={aiLoading}
            rewriteLoading={rewriteLoading}
            rewriteContext={rewriteContext}
            setRewriteContext={setRewriteContext}
            suggestTemplate={suggestTemplate}
            setSuggestTemplate={setSuggestTemplate}
            onAiSuggest={getSuggestion}
            onAiRewrite={getRewrite}
            channelWarning={
              selectedTicket?.channel === 'ebay' ? `Reply will also be sent via eBay messaging${selectedTicket.channel_buyer_username ? ` to ${selectedTicket.channel_buyer_username}` : ''}` :
              selectedTicket?.channel === 'reverb' ? `Reply will also be sent via Reverb messaging${selectedTicket.channel_buyer_username ? ` to ${selectedTicket.channel_buyer_username}` : ''}` :
              null
            }
            onSend={async (statusAfter) => {
              // sendReply returns true only when the backend accepted the
              // reply — only then do we apply the optional status change.
              // Composer stays open on failure so the operator can retry
              // without losing what they typed.
              const ok = await sendReply()
              if (!ok) return
              if (statusAfter) updateTicketStatus(selectedTicket.id, statusAfter)
              setComposerOpen(false)
            }}
            onCancel={() => setComposerOpen(false)}
          />
        )}
      </div>
    )
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

      {/* Assignment Bar */}
      {ticketDetails && !isMobile && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 16px',
          marginBottom: 8,
          background: 'var(--bg)',
          borderRadius: 8,
          border: '1px solid rgba(0,0,0,.08)',
          fontSize: 13
        }}>
          {/* Left: Subject + last updated */}
          <div style={{ flex: '1 1 0', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: '1 1 auto' }}>
                {ticketDetails.ticket.subject || 'No subject'}
              </div>
              {(() => {
                const orderRef = ticketOrderRef(ticketDetails.ticket)
                return orderRef ? (
                  <button
                    type="button"
                    onClick={() => openTicketOrder(orderRef)}
                    title={`Open order ${orderRef}`}
                    style={{
                      flex: '0 0 auto',
                      background: '#dcfce7',
                      color: '#166534',
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 12,
                      whiteSpace: 'nowrap',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Order #{orderRef}
                  </button>
                ) : null
              })()}
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
              Updated {formatDate(ticketDetails.ticket.updated_at || ticketDetails.ticket.created_at)}
            </div>
          </div>

          {/* Center: Assigned to */}
          <div style={{ flex: '0 0 auto', textAlign: 'center', padding: '0 24px' }}>
            <span style={{ color: 'var(--muted)', fontWeight: 500 }}>Assigned to: </span>
            {ticketDetails.ticket.assigned_to ? (
              <span style={{ fontWeight: 600 }}>
                {getUserName(ticketDetails.ticket.assigned_to)}
                {ticketDetails.ticket.assigned_to === user?.user_id && (
                  <span style={{ color: 'var(--color-primary, #2563eb)', marginLeft: 4 }}>(you)</span>
                )}
              </span>
            ) : (
              <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>Unassigned</span>
            )}
          </div>

          {/* Right: topic, then reassign */}
          <div style={{ flex: '1 1 0', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <TopicPicker
              topic={ticketDetails.ticket.topic}
              onChange={topic => handleTopicChange(selectedTicket.id, topic)}
            />
            <label htmlFor="reassign-ticket" style={{ color: 'var(--muted)', fontWeight: 500, whiteSpace: 'nowrap' }}>Assign to:</label>
            <select
              id="reassign-ticket"
              className="form-input"
              value={ticketDetails.ticket.assigned_to || ''}
              onChange={e => handleAssign(selectedTicket.id, e.target.value ? parseInt(e.target.value, 10) : null)}
              style={{ width: 'auto', minWidth: 140, fontSize: 12 }}
              aria-label="Assign ticket to user"
            >
              <option value="">Unassigned</option>
              {ticketUsers.map(u => (
                <option key={u.user_id || u.id} value={u.user_id || u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>

        {/* Message Thread */}
        {(
          <section
            className="card conversationColumn"
            style={{
              padding: 0,
              display: isMobile && mobileView !== 'detail' ? 'none' : 'flex'
            }}
          >
            {loadingDetails ? (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <div className="loading-spinner" />
              </div>
            ) : ticketDetails && (
              <>
                {/* Mobile back button */}
                {isMobile && (
                  <div style={{ padding: '8px 16px', borderBottom: '1px solid rgba(0,0,0,.06)' }}>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => setMobileView('list')}
                      style={{ padding: '4px 8px' }}
                    >
                      ← Back to list
                    </button>
                  </div>
                )}

                <div className="messageList" style={{ background: 'var(--bg)' }}>
                  {/* Reverb conversation thread — only on channel='reverb' tickets.
                      Newest message gets a prominent callout; full thread shown
                      below for context. Refresh button re-pulls from Reverbs API. */}
                  {ticketDetails.ticket.channel === 'reverb' && (
                    <ReverbThreadSection
                      thread={reverbThread}
                      status={reverbThreadStatus}
                      reason={reverbThreadReason}
                      fetchedAt={reverbThreadFetchedAt}
                      refreshing={reverbThreadRefreshing}
                      onRefresh={async () => {
                        setReverbThreadRefreshing(true)
                        try {
                          const res = await api.post(`/api/tickets/${ticketDetails.ticket.id}/reverb-thread/refresh`)
                          if (res?.thread) {
                            setReverbThread(res.thread)
                            setReverbThreadFetchedAt(res.fetchedAt || null)
                            setReverbThreadStatus('ready')
                            setReverbThreadReason(null)
                          } else {
                            setReverbThreadStatus('unavailable')
                            setReverbThreadReason(res?.reason || 'no thread available')
                          }
                        } catch (err) {
                          setReverbThreadStatus('error')
                          setReverbThreadReason(err.message || 'request failed')
                        } finally {
                          setReverbThreadRefreshing(false)
                        }
                      }}
                    />
                  )}

                  {/* Replies (newest first) */}
                  {[...(ticketDetails.replies || [])].reverse().map(reply => (
                    <div
                      key={reply.id}
                      style={{
                        background: reply.direction === 'out' ? '#e8f5e9' : 'white',
                        padding: 16,
                        borderRadius: 8,
                        marginBottom: 16,
                        marginLeft: reply.direction === 'out' ? 32 : 0,
                        marginRight: reply.direction === 'in' ? 32 : 0
                      }}
                    >
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                        {reply.direction === 'in'
                          ? <>{authorOf(reply)}<EmailTimes row={reply} /></>
                          : <>{authorOf(reply)} - {formatDate(reply.sent_at)}</>}
                        {reply.template_used && (
                          <span style={{ marginLeft: 8, fontStyle: 'italic' }}>
                            (Template: {reply.template_used})
                          </span>
                        )}
                      </div>
                      <EmailContent
                        html={reply.body_html}
                        text={reply.body_text}
                      />
                      {reply.attachments && (() => {
                        const attachments = typeof reply.attachments === 'string'
                          ? JSON.parse(reply.attachments)
                          : reply.attachments
                        return attachments.length > 0 && (
                          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,.1)' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                              Attachments ({attachments.length})
                              {attachments.filter(a => a.attachmentId).length > 1 && (
                                <DownloadAllAttachments ticketId={ticketDetails.ticket.id} attachments={attachments} />
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {attachments.map((att, idx) => (
                                <AttachmentThumbnail
                                  key={idx}
                                  ticketId={ticketDetails.ticket.id}
                                  attachment={att}
                                />
                              ))}
                            </div>
                          </div>
                        )
                      })()}
                    </div>
                  ))}

                  {/* Original message (oldest, at bottom) */}
                  <div style={{ background: 'white', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                      <EmailTimes row={ticketDetails.ticket} />
                    </div>
                    <EmailContent
                      html={ticketDetails.ticket.body_html}
                      text={ticketDetails.ticket.body_text}
                      fallback={ticketDetails.ticket.snippet}
                    />
                    {/* Attachments */}
                    {ticketDetails.ticket.attachments && (() => {
                      const attachments = typeof ticketDetails.ticket.attachments === 'string'
                        ? JSON.parse(ticketDetails.ticket.attachments)
                        : ticketDetails.ticket.attachments
                      return attachments.length > 0 && (
                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,.1)' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                            Attachments ({attachments.length})
                            {attachments.filter(a => a.attachmentId).length > 1 && (
                              <DownloadAllAttachments ticketId={ticketDetails.ticket.id} attachments={attachments} />
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {attachments.map((att, idx) => (
                              <AttachmentThumbnail
                                key={idx}
                                ticketId={ticketDetails.ticket.id}
                                attachment={att}
                              />
                            ))}
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </>
            )}
          </section>
        )}

        {/* Reply Column */}
        {selectedTicket && ticketDetails && (
          <section
            className="card replyColumn"
            style={{
              display: isMobile && mobileView !== 'detail' ? 'none' : undefined
            }}
          >
            <div className="quickReplySection">
              <div className="quickReplyHeader">
                <div className="quickReplyTitle">Quick Replies</div>
                <div className="quickReplyButtons">
                  <div style={{ display: 'flex', gap: 0 }}>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={getSuggestion}
                      disabled={aiLoading}
                      title="Get AI-suggested response based on customer history and email content"
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    >
                      {aiLoading ? '🤖 Thinking...' : '🤖 AI Suggest'}
                    </button>
                    <select
                      value={suggestTemplate}
                      onChange={e => setSuggestTemplate(e.target.value)}
                      className="form-input"
                      style={{
                        padding: '4px 6px',
                        fontSize: 11,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderLeft: 'none',
                        width: 'auto',
                        minWidth: 80
                      }}
                      title="Reply template for AI Suggest"
                      aria-label="AI Suggest template"
                    >
                      <option value="auto">Auto</option>
                      <option value="market_price">Market price</option>
                      <option value="counter_offer">Counter offer</option>
                      <option value="fair_price">Ask fair price</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 0 }}>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => getRewrite()}
                      disabled={rewriteLoading || !replyText.trim()}
                      title="Rewrite your draft to be more professional and polished"
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    >
                      {rewriteLoading ? '✨ Rewriting...' : '✨ AI Rewrite'}
                    </button>
                    <select
                      value={rewriteContext}
                      onChange={e => setRewriteContext(e.target.value)}
                      className="form-input"
                      style={{
                        padding: '4px 6px',
                        fontSize: 11,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderLeft: 'none',
                        width: 'auto',
                        minWidth: 80
                      }}
                      title="Select context for AI rewrite"
                    >
                      <option value="auto">Auto</option>
                      <option value="trade_in">Trade-In</option>
                      <option value="order">Order</option>
                    </select>
                  </div>
                  <FaqLinkPicker
                    topic={ticketDetails?.ticket?.topic ?? selectedTicket?.topic}
                    replyText={replyText}
                    setReplyText={setReplyText}
                  />
                </div>
              </div>

              {selectedTicket?.channel === 'ebay' && (
                <div style={{ padding: '6px 10px', borderRadius: 4, background: '#EBF5FF', border: '1px solid #0654ba33', fontSize: 12, color: '#0654ba', marginBottom: 6 }}>
                  Reply will also be sent via eBay messaging{selectedTicket.channel_buyer_username ? ` to ${selectedTicket.channel_buyer_username}` : ''}
                  {!selectedTicket.channel_item_id && <span style={{ color: '#B91C1C', marginLeft: 6 }}>(item ID missing — eBay send may fail)</span>}
                </div>
              )}
              {selectedTicket?.channel === 'reverb' && (
                <div style={{ padding: '6px 10px', borderRadius: 4, background: '#F3E8FF', border: '1px solid #6b21a833', fontSize: 12, color: '#6b21a8', marginBottom: 6 }}>
                  Reply will also be sent via Reverb messaging{selectedTicket.channel_buyer_username ? ` to ${selectedTicket.channel_buyer_username}` : ''}
                </div>
              )}
              {composerNotices}
              <textarea
                className="form-input replyTextarea"
                placeholder="Type your reply..."
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, marginBottom: 4 }}>
                <label
                  className="btn btn-sm btn-secondary"
                  style={{ cursor: 'pointer', margin: 0 }}
                >
                  Attach File
                  <input
                    type="file"
                    style={{ display: 'none' }}
                    onChange={e => setReplyFile(e.target.files[0] || null)}
                  />
                </label>
                {replyFile && (
                  <span style={{ fontSize: 12, color: '#374151' }}>
                    {replyFile.name}
                    <button
                      onClick={() => setReplyFile(null)}
                      style={{ marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontWeight: 'bold', padding: 0 }}
                    >
                      &times;
                    </button>
                  </span>
                )}
              </div>

              <div className="replyActions">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}
                  >
                    Resolve
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setShowPinModal(true)
                      setPinData({ reason: '', dueDate: '', assignedTo: '' })
                      if (user?.is_admin) {
                        api.get('/api/users').then(res => {
                          setPinUsers(activeUsersFrom(res))
                        }).catch(err => console.error('Failed to load users:', err))
                      }
                    }}
                    title="Pin this ticket for follow-up"
                  >
                    Pin
                  </button>
                  <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,.15)', margin: '0 4px' }} />
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => markAsService('email')}
                    title="Route future emails from this address to Service"
                  >
                    Service (Email)
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => markAsService('domain')}
                    title="Route future emails from this domain to Service"
                  >
                    Service (Domain)
                  </button>
                  <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,.15)', margin: '0 4px' }} />
                  <button
                    className="btn btn-sm"
                    style={{ background: '#EF4444', color: 'white' }}
                    onClick={() => ignoreSender('email')}
                    title="Never import emails from this address"
                  >
                    Block Email
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{ background: '#EF4444', color: 'white' }}
                    onClick={() => ignoreSender('domain')}
                    title="Never import emails from this domain"
                  >
                    Block Domain
                  </button>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={sendReply}
                  disabled={!replyText.trim() || sending}
                >
                  {sending ? 'Sending...' : 'Send Reply'}
                </button>
              </div>
            </div>
          </section>
        )}

      </div>
    </div>
  )
}

export default TicketConversation
